import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  Content,
  RankingUserSection,
  RankingTableSection,
  RankingTable,
  TableHeader,
  TableBody,
  Position,
  UserCell,
  PointsCell,
  TopUsers,
  PartyRanking,
  Avatar,
  UserInfo,
  Podium,
  Points,
  TitlePanel,
} from './styles';

import {
  ChartIcon,
  FirstPodiumIcon,
  SecondPodiumIcon,
  ThirdPodiumIcon,
} from '../../assets/svg/SVGComponents';
import PrimaryButton from '../Buttons/PrimaryButton';
import { IUserRankingProps } from '../../dtos/IUserRankingProps';
import LoadingComponent from './LoadingComponent';

const RankingPanel: React.FC<{
  top_users?: IUserRankingProps[];
  isLoading: boolean;
}> = ({ top_users, isLoading }) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkScrollPosition = (): void => {
      if (hasAnimated.current) return; // Verifica apenas uma vez

      if (componentRef.current) {
        const rect = componentRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        // Ativa animação quando o topo do componente está na metade inferior da tela
        if (rect.top < windowHeight * 0.75) {
          setIsVisible(true);
          hasAnimated.current = true;

          // Remove o event listener após ativar
          window.removeEventListener('scroll', checkScrollPosition);
        }
      }
    };

    // Verificar imediatamente após montagem (pode estar já visível)
    checkScrollPosition();

    // Adicionar event listener para verificar durante o scroll
    window.addEventListener('scroll', checkScrollPosition);

    // Limpar event listener na desmontagem
    return () => {
      window.removeEventListener('scroll', checkScrollPosition);
    };
  }, []);

  const rankingData = useMemo(() => {
    const users = top_users || [];
    // Ordenar os usuários por rank (caso não venham ordenados)
    const sortedUsers = [...users].sort((a, b) => a.rank - b.rank);

    // Buscar as posições específicas
    const firstplace = sortedUsers.find(user => user.rank === 1);
    const secondplace = sortedUsers.find(user => user.rank === 2);
    const thirdplace = sortedUsers.find(user => user.rank === 3);

    // Se não encontrar pelos ranks, usar os primeiros da lista ordenada
    const fallbackFirst = sortedUsers[0];
    const fallbackSecond = sortedUsers[1];
    const fallbackThird = sortedUsers[2];

    const otherUsers = sortedUsers.filter(
      user => user.rank > 3 && user.rank <= 10,
    );

    return {
      firstplace: firstplace || fallbackFirst,
      secondplace: secondplace || fallbackSecond,
      thirdplace: thirdplace || fallbackThird,
      otherUsers,
    };
  }, [top_users]);

  useEffect(() => {
    if (
      !rankingData.firstplace &&
      !rankingData.secondplace &&
      !rankingData.thirdplace
    ) {
      // No ranking data available
    }
  }, [rankingData]);

  if (isLoading) {
    return (
      <Container>
        <LoadingComponent />
      </Container>
    );
  }
  return (
    <Container ref={componentRef}>
      <Header>
        <div className="header-comunity">
          <TitlePanel>
            <ChartIcon
              width="2rem"
              stroke="#FF9800"
              strokeWidth={2}
              fill="none"
            />
            <h1>Ranking</h1>
          </TitlePanel>
          <span>Ranking de pontuações de educadores.</span>
        </div>

        <PrimaryButton onClick={() => navigate('/ranking')}>
          Visitar Ranking
        </PrimaryButton>
      </Header>
      {rankingData && (
        <Content>
          <RankingUserSection>
            <TopUsers>
              {rankingData.thirdplace && (
                <PartyRanking
                  className={
                    isVisible
                      ? 'animate-podium animate-delay-2'
                      : 'hidden-podium'
                  }
                >
                  <Avatar $color="#CD7F32">
                    <img
                      src={
                        rankingData.thirdplace.avatar_url ||
                        'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/defaultUserImage.png'
                      }
                      alt="User Avatar"
                    />
                    <UserInfo>
                      <h2>{rankingData.thirdplace.name}</h2>
                      <p>{rankingData.thirdplace.school_name}</p>
                    </UserInfo>
                  </Avatar>
                  <Podium>
                    <Points>{rankingData.thirdplace.total_points} pts</Points>
                    <ThirdPodiumIcon width="7.5rem" />
                  </Podium>
                </PartyRanking>
              )}
              {rankingData.firstplace && (
                <PartyRanking
                  className={isVisible ? 'animate-podium' : 'hidden-podium'}
                >
                  <Avatar $color="#FFD700">
                    <img
                      src={
                        rankingData.firstplace.avatar_url ||
                        'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/defaultUserImage.png'
                      }
                      alt="User Avatar"
                    />
                    <UserInfo>
                      <h2>{rankingData.firstplace.name}</h2>
                      <p>{rankingData.firstplace.school_name}</p>
                    </UserInfo>
                  </Avatar>
                  <Podium>
                    <Points>{rankingData.firstplace.total_points} pts</Points>
                    <FirstPodiumIcon width="7.5rem" />
                  </Podium>
                </PartyRanking>
              )}
              {rankingData.secondplace && (
                <PartyRanking
                  className={
                    isVisible
                      ? 'animate-podium animate-delay-1'
                      : 'hidden-podium'
                  }
                >
                  <Avatar $color="#B5B5B5">
                    <img
                      src={
                        rankingData.secondplace.avatar_url ||
                        'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/defaultUserImage.png'
                      }
                      alt="User Avatar"
                    />
                    <UserInfo>
                      <h2>{rankingData.secondplace.name}</h2>
                      <p>{rankingData.secondplace.school_name}</p>
                    </UserInfo>
                  </Avatar>
                  <Podium>
                    <Points>{rankingData.secondplace.total_points} pts</Points>
                    <SecondPodiumIcon width="7.5rem" />
                  </Podium>
                </PartyRanking>
              )}
            </TopUsers>
          </RankingUserSection>
          <RankingTableSection>
            <RankingTable>
              <TableHeader>
                <tr>
                  <th style={{ width: '10%', textAlign: 'justify' }}>
                    Posição
                  </th>
                  <th style={{ width: '50%' }}>Educadores</th>
                  <th style={{ width: '20%', textAlign: 'center' }}>Pontos</th>
                </tr>
              </TableHeader>
              <TableBody>
                {rankingData.otherUsers.map((user, index) => (
                  <tr
                    key={user.id}
                    className={
                      isVisible
                        ? `animate-row animate-delay-${index + 3}`
                        : 'hidden-row'
                    }
                  >
                    <td style={{ textAlign: 'center' }}>
                      <Position $position={user.rank}>{user.rank}</Position>
                    </td>
                    <UserCell>
                      <img
                        src={
                          user.avatar_url ||
                          'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/defaultUserImage.png'
                        }
                        alt={`${user.name}'s avatar`}
                      />
                      <div>
                        <h3>{user.name}</h3>
                      </div>
                    </UserCell>

                    <PointsCell>{user.total_points} pts</PointsCell>
                  </tr>
                ))}
              </TableBody>
            </RankingTable>
          </RankingTableSection>
        </Content>
      )}
    </Container>
  );
};

export default RankingPanel;
